import { $, $$ } from '../lib/dom.js';
import { reduceMotion } from '../lib/env.js';

/* Capa de navegación. No viene de la plantilla de diseño: ahí los enlaces
   simplemente desaparecían abajo de 760 px y nada indicaba la sección actual. */

const BREAKPOINT = '(min-width: 761px)';

/* Cuánto por debajo de la barra está la "línea de lectura" del scroll spy.
   Tiene que quedar holgada: al saltar a un ancla la sección se apoya en el
   scroll-padding de la raíz (--nav-height sin condensar), que es más alto que
   la barra ya condensada; con un margen chico la sección de destino quedaba
   apenas por debajo de la línea y se marcaba la anterior. */
const READING_OFFSET = 24;

/* Alto real de la barra — cambia cuando se condensa al salir del hero. */
function navHeight() {
  const inner = $('.pv-nav__inner');
  return inner ? inner.getBoundingClientRect().height : 68;
}

/* Corre `fn` a lo sumo una vez por frame mientras se scrollea. */
function onScroll(fn) {
  let ticking = false;
  function tick() {
    ticking = false;
    fn();
  }
  function request() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(tick);
  }
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request, { passive: true });
  fn();
}

/* ---------- Menú mobile ---------- */
function initMenu() {
  const menu = $('[data-menu]');
  const toggle = $('[data-menu-open]');
  if (!menu || !toggle) return;

  const supportsDialog = typeof menu.showModal === 'function';
  const panel = $('[data-menu-panel]', menu);

  function open() {
    document.body.classList.add('pv-menu-open');
    toggle.setAttribute('aria-expanded', 'true');
    if (supportsDialog) menu.showModal();
    else menu.setAttribute('open', '');
  }

  function close() {
    if (supportsDialog && menu.open) menu.close();
    else menu.removeAttribute('open');
    cleanup();
  }

  function cleanup() {
    document.body.classList.remove('pv-menu-open');
    toggle.setAttribute('aria-expanded', 'false');
  }

  toggle.addEventListener('click', open);
  $$('[data-menu-close]', menu).forEach((btn) => btn.addEventListener('click', close));

  // Al elegir una sección el menú se va y el ancla hace el resto.
  $$('[data-menu-link]', menu).forEach((link) => link.addEventListener('click', close));

  // Click fuera del panel (la franja translúcida de abajo).
  menu.addEventListener('click', (e) => {
    if (panel && !panel.contains(e.target)) close();
  });

  menu.addEventListener('close', cleanup);
  if (!supportsDialog) {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.hasAttribute('open')) close();
    });
  }

  // Si la ventana crece por encima del breakpoint, el menú deja de tener sentido.
  const desktop = window.matchMedia(BREAKPOINT);
  const onChange = (e) => {
    if (e.matches && (menu.open || menu.hasAttribute('open'))) close();
  };
  if (desktop.addEventListener) desktop.addEventListener('change', onChange);
  else if (desktop.addListener) desktop.addListener(onChange);
}

/* ---------- Sección activa ---------- */
function initSpy() {
  const links = $$('[data-spy]');
  if (!links.length) return;

  // Cada sección puede tener más de un enlace apuntándola (nav y menú mobile).
  const byId = new Map();
  links.forEach((link) => {
    const id = (link.getAttribute('href') || '').replace('#', '');
    const section = id && document.getElementById(id);
    if (!section) return;
    if (!byId.has(id)) byId.set(id, { section, links: [] });
    byId.get(id).links.push(link);
  });
  if (!byId.size) return;

  const order = Array.from(byId.values());
  let painted = null;

  function paint() {
    // Activa = la sección que cruza la línea de lectura, justo debajo de la barra.
    // Con secciones contiguas hay una sola; arriba del primero (hero) o abajo del
    // último (contacto) no hay ninguna y no se marca nada, en vez de dejar
    // encendida la anterior.
    const line = navHeight() + READING_OFFSET;
    let current = null;
    for (const entry of order) {
      const rect = entry.section.getBoundingClientRect();
      if (rect.top <= line && rect.bottom > line) current = entry;
    }
    if (current === painted) return;
    painted = current;

    order.forEach((entry) => {
      entry.links.forEach((link) => {
        if (entry === current) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    });
  }

  onScroll(paint);
}

/* ---------- Barra condensada + volver arriba ---------- */
function initScrollState() {
  const nav = $('[data-nav]');
  const totop = $('[data-totop]');
  const hero = document.getElementById('hero');
  if (!hero || (!nav && !totop)) return;

  function setPastHero(past) {
    if (nav) nav.classList.toggle('is-compact', past);
    if (totop) totop.hidden = !past;
  }

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => setPastHero(!entry.isIntersecting)),
      { threshold: 0 }
    );
    io.observe(hero);
  } else {
    setPastHero(false);
  }

  if (!totop) return;
  // El href a #hero queda como fallback sin JS, pero un ancla al mismo hash que
  // ya está en la URL no vuelve a scrollear: del segundo clic en adelante no
  // pasaría nada. Acá lo resolvemos a mano y de paso no ensuciamos el historial.
  totop.addEventListener('click', (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    const logo = $('.pv-nav__logo');
    if (logo) logo.focus({ preventScroll: true });
  });
}

export function initNav() {
  initMenu();
  initSpy();
  initScrollState();
}
